import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, CardImg, CardTitle } from 'reactstrap';

import { useWLC } from '../../../../routes/Products/products/WLC/contexts/WLCContext';
import getContent from '../../../../lib/getContent';

const EXTERNAL_RESOURCE_TYPES = ['link', 'download', 'zoomMeeting'];

const getUrlFromContent = content => {
  return content?.data?.url || content?.data?.recordingUrl || content?.url || content?.recordingUrl || null;
};

const getNavConfig = ({ slug, url }) => {
  if (!url) return { navSlug: slug, navType: null };
  const navType = /^https?:\/\//i.test(url) ? 'ExternalLink' : 'InternalLink';
  return { navSlug: url, navType };
};

const GlobalSearchResultCard = ({ item }) => {
  const { authToken, handleNav, productSlug } = useWLC();
  const [isLoading, setIsLoading] = useState(false);
  const { image, slug, title, type, data } = item;

  const handleClick = async () => {
    if (!slug || isLoading) return;

    if (!EXTERNAL_RESOURCE_TYPES.includes(type)) {
      handleNav(null, null, slug);
      return;
    }

    try {
      setIsLoading(true);

      const content = await getContent(slug, productSlug, authToken);
      const urlFromApi = getUrlFromContent(content);
      const fallbackUrl = data?.url || data?.recordingUrl || null;
      const { navSlug, navType } = getNavConfig({ slug, url: urlFromApi || fallbackUrl });

      if (!navSlug) {
        handleNav(null, null, slug);
        return;
      }

      handleNav(null, navType, navSlug);
    } catch (error) {
      console.error('GlobalSearchResultCard: failed to load content for resource navigation', error);
      handleNav(null, null, slug);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      className="pointerGlobal"
      onClick={handleClick}
      role="button"
      aria-busy={isLoading}
      style={{
        pointerEvents: isLoading ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
        height: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {isLoading ? (
        <div
          aria-live="polite"
          className="d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            inset: 0,
            position: 'absolute',
            zIndex: 1
          }}
        >
          <small className="text-muted">Loading...</small>
        </div>
      ) : null}
      <CardImg
        alt={title || 'Search result image'}
        src={image?.url || ''}
        top
        style={{ aspectRatio: '16/9', width: '100%', objectFit: 'cover' }}
      />
      <CardBody className="d-flex align-items-center">
        <CardTitle tag="h6" className="mb-0 w-100">
          {title}
        </CardTitle>
      </CardBody>
    </Card>
  );
};

GlobalSearchResultCard.propTypes = {
  item: PropTypes.shape({
    image: PropTypes.shape({
      url: PropTypes.string
    }),
    slug: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    data: PropTypes.shape({
      url: PropTypes.string,
      recordingUrl: PropTypes.string
    })
  }).isRequired
};

export default GlobalSearchResultCard;
